import * as AgGrid from "ag-grid-community";
import * as gridUtils from "grid/utils";
import * as datasource from "grid/datasource";
import * as rowActions from "./row_actions";
import { GridController } from "grid/grid_controller";
import { DEFAULT_COL_DEF, ACTIONS_COL_DEF } from "../../grid/columns";

interface Group {
  id: string;
  name: string;
  update_url?: string;
  remove_url?: string;
}

export default class extends GridController<Group> {
  connect() {
    super.connect();
  }

  override buildGrid() {
    const { i18nJson, paginationPageSize, groups, confirmDelete } = this.gridTarget.dataset;
    const i18n = JSON.parse(i18nJson || "{}");

    const colDefs: AgGrid.ColDef[] = [
      { field: "id", ...DEFAULT_COL_DEF, sortable: false },
      { field: "name", ...DEFAULT_COL_DEF, sortable: false, editable: true },
      {
        ...ACTIONS_COL_DEF,
        cellClass: "actions-cell",
        onCellClicked: (params) => {
          rowActions.onActionCellClicked(params, async (action) => {
            if (action === rowActions.Action.EDIT) {
              gridApi.startEditingCell({ rowIndex: params.node.rowIndex, colKey: "name" });
              this.#toggleRowEditingUi(params, true);
              return;
            }

            if (action === rowActions.Action.APPLY) {
              gridApi.stopEditing();
              this.#toggleRowEditingUi(params, false);
              return;
            }

            if (action === rowActions.Action.REMOVE) {
              const msgTemplate = confirmDelete || "Delete group?";
              const msg = msgTemplate.includes("%{name}")
                ? msgTemplate.replace("%{name}", params.node.data.name || "")
                : msgTemplate;
              if (!confirm(msg)) return;

              fetch(params.node.data.remove_url, {
                method: "DELETE",
                headers: {
                  "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
                },
              }).then((response) => {
                gridApi.purgeInfiniteCache();
              });
            }
          });
        },
      },
    ];

    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });

    const options: AgGrid.GridOptions<Group> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<Group, any>),
      },
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n }),
      pagination: true,
      paginationPageSize: parseInt(paginationPageSize),
      datasource: dataSource,
      suppressClickEdit: true,
      onCellEditingStopped: (event) => {
        if (event.colDef.field !== "name") return;
        if (!event.data?.update_url) return;
        if (event.newValue === event.oldValue) return;

        fetch(event.data.update_url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
          },
          body: JSON.stringify({ group: { name: event.newValue } }),
        }).then(() => {
          gridApi.purgeInfiniteCache();
        });
      },
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, {
      ...options,
    });

    dataSource.gridApi = gridApi;
    return gridApi;
  }

  #toggleRowEditingUi(params: AgGrid.CellClickedEvent<Group>, isEditing: boolean) {
    const cellEl = gridUtils.actionsCell(params) as HTMLElement | undefined;
    if (!cellEl) return;

    const editBtn = cellEl.querySelector<HTMLButtonElement>('button[data-action="edit"]');
    const applyBtn = cellEl.querySelector<HTMLButtonElement>('button[data-action="apply"]');
    if (!editBtn || !applyBtn) return;

    editBtn.classList.toggle("hidden", isEditing);
    applyBtn.classList.toggle("hidden", !isEditing);
  }
}
